import { useLayoutEffect, useRef, useState, type ReactNode } from "react"

import type { Participant } from "@vc/models"
import { layoutRows, TILE_ASPECT } from "./tile-geometry"
import { TileLayer, type TileSlot } from "./TileLayer"
import { VideoFitDefaultContext } from "./video-fit"

const RAIL_GAP = 8
/** Room kept above and below the tiles for the rail's page arrows. */
const RAIL_PAD_Y = 48

type RailTilesProps = {
  /** The page being shown, already split into rows by the caller. */
  rows: readonly (readonly Participant[])[]
  /** Height of every row, from the same calculation that split the rows. */
  rowHeight: number
  renderTile: (participant: Participant) => ReactNode
  /** Makes each tile a button; the stage layouts pin whoever is clicked. */
  onSelect?: (participantId: string) => void
  /** Vertical room reserved for the page arrows. */
  padY?: number
  slotClassName?: string
}

function useElementSize() {
  const ref = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })

  // Measured before paint, then kept current; the rail is dragged to resize,
  // so every frame of the drag has to reach the layout.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const apply = (width: number, height: number) =>
      setSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }))
    const rect = el.getBoundingClientRect()
    apply(rect.width, rect.height)
    const ro = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect
      if (box) apply(box.width, box.height)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return [ref, size] as const
}

/**
 * Participant tiles stacked in a side rail or pinned area.
 *
 * Every stage layout (whiteboard, screen share, document, video, sidebar,
 * pinned) shows the rest of the room in rows beside the main content. They all
 * used to draw those rows as nested flex containers keyed by row index, so a
 * participant moving to another row — the rail filling up, the speaker
 * changing — was unmounted and mounted again, with a blank frame in between.
 *
 * Here the rows are only geometry: tiles are siblings in one TileLayer, keyed
 * by participant, and glide between rows instead of being rebuilt. The shape
 * is the design's: rows share the height evenly, tiles fill their row, widths
 * are capped at 16:9 and centred, with the same gaps and padding.
 */
export function RailTiles({
  rows,
  rowHeight,
  renderTile,
  onSelect,
  padY = RAIL_PAD_Y,
  slotClassName = "ring-1 ring-white/10",
}: RailTilesProps) {
  const [ref, { width, height }] = useElementSize()

  const rowCounts = rows.map((row) => row.length)
  const rects =
    width > 0 && height > 0
      ? layoutRows(rowCounts, {
          width,
          height,
          gap: RAIL_GAP,
          rowHeight,
          // As wide as the rail allows, capped at 16:9 for the row's height,
          // and always the row's full height — the rail as designed.
          tileWidth: (_row, n) => Math.min((width - RAIL_GAP * (n - 1)) / n, rowHeight * TILE_ASPECT),
          fillRowHeight: true,
          padTop: padY,
          padBottom: padY,
        })
      : []

  const people = rows.flat()
  const slots: TileSlot[] =
    rects.length === people.length
      ? people.map((p, i) => ({
          key: p.id,
          rect: rects[i],
          content: renderTile(p),
          className: onSelect ? `${slotClassName} hover:ring-white/20` : slotClassName,
          title: onSelect ? p.name : undefined,
          onClick: onSelect ? () => onSelect(p.id) : undefined,
        }))
      : []

  return (
    <div ref={ref} className="h-full w-full">
      {/* Filled rows show the camera edge to edge, as designed. */}
      <VideoFitDefaultContext.Provider value="fill">
        <TileLayer slots={slots} width={width} height={height} />
      </VideoFitDefaultContext.Provider>
    </div>
  )
}
